import React, { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { markLeadsViewed } from '../actions/registerUser';
import { formatDateTime, formatListStamp } from '../utils/formatDate';
import Spinner from './Spinner';
import AppTopBar from './AppTopBar';
import BackButton from './BackButton';
import './Leads.css';

const AVATAR_COLORS = ['#6b7fd7', '#4b9a6f', '#c17d3a', '#c15b7a', '#4a9dab', '#9068c9'];

const getInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const colorForLead = (id) => {
  const str = String(id || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const waLink = (e164) => {
  if (!e164) return null;
  // wa.me wants digits only, no leading '+'.
  return `https://wa.me/${e164.replace(/^\+/, '')}`;
};

const Leads = ({ isAuth, loading, markLeadsViewed }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async (targetPage) => {
    setStatus('loading');
    try {
      const res = await axios.get('/api/users/leads', { params: { page: targetPage } });
      setData(res.data);
      setStatus('ready');
    } catch (err) {
      setErrorMsg(
        (err.response && err.response.data && err.response.data.msg) ||
          'Failed to load leads.'
      );
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  // Opening /leads is what clears the badge -- fire once on mount, not
  // per page change, so paging through results doesn't re-send it.
  useEffect(() => {
    markLeadsViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/api/users/leads/${id}`);
      setData((current) => ({
        ...current,
        leads: current.leads.filter((l) => l._id !== id),
        total: current.total - 1,
      }));
      setExpandedId((current) => (current === id ? null : current));
    } catch (err) {
      alert('Failed to delete this lead. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!loading && !isAuth) return <Redirect to="/login" />;
  if (loading) return <Spinner />;

  return (
    <div className="leads-page">
      {/* Same Bootstrap .container Dashboard.js/EditProfile.js wrap
          AppTopBar in, so the bar renders at an identical width on every
          page -- deliberately not .leads-page__inner below (max-width:
          640px), which is narrower and would create a new mismatch. */}
      <div className="container" style={{ padding: '0' }}>
        <AppTopBar back={<BackButton to="/edit_profile" />} />
      </div>
      <div className="leads-page__inner">
        {status === 'loading' && <Spinner />}

        {status === 'error' && <p style={{ color: '#d1453b' }}>{errorMsg}</p>}

        {status === 'ready' && data.leads.length === 0 && (
          <div className="leads-empty">
            <div className="leads-empty__icon">
              <span role="img" aria-label="Waving hand">👋</span>
            </div>
            <h3>No leads yet</h3>
            <p>
              When a visitor taps "Share your details" on your public profile
              and submits the form, their name and contact info will show up
              here so you can follow up -- nothing to set up, it just starts
              arriving.
            </p>
          </div>
        )}

        {status === 'ready' && data.leads.length > 0 && (
          <>
            <div className="leads-list">
              {data.leads.map((lead) => {
                const isOpen = expandedId === lead._id;
                return (
                  <React.Fragment key={lead._id}>
                    <div
                      className={`lead-row${isOpen ? ' lead-row--open' : ''}`}
                      onClick={() => setExpandedId(isOpen ? null : lead._id)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') setExpandedId(isOpen ? null : lead._id);
                      }}
                    >
                      <div
                        className="lead-row__avatar"
                        style={{ backgroundColor: colorForLead(lead._id) }}
                      >
                        {getInitials(lead.name)}
                      </div>
                      <div className="lead-row__body">
                        <p className="lead-row__name">{lead.name}</p>
                        <p
                          className={`lead-row__preview${
                            lead.message ? '' : ' lead-row__preview--empty'
                          }`}
                        >
                          {lead.message || 'No message'}
                        </p>
                      </div>
                      <div className="lead-row__meta">
                        <span className="lead-row__date">
                          {formatListStamp(lead.createdAt)}
                        </span>
                        <span className="lead-row__chevron">▶</span>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="lead-detail">
                        {lead.message && (
                          <p className="lead-detail__message">{lead.message}</p>
                        )}

                        <div className="lead-detail__row">
                          <span className="lead-detail__label">Email</span>
                          <span>{lead.email}</span>
                        </div>
                        <div className="lead-detail__row">
                          <span className="lead-detail__label">Phone</span>
                          <span>{lead.phone || '—'}</span>
                        </div>
                        <div className="lead-detail__row">
                          <span className="lead-detail__label">WhatsApp</span>
                          <span>{lead.whatsapp || '—'}</span>
                        </div>
                        <div className="lead-detail__row">
                          <span className="lead-detail__label">Received</span>
                          <span>{formatDateTime(lead.createdAt)}</span>
                        </div>

                        <div className="lead-detail__actions">
                          <a
                            className="lead-action-btn lead-action-btn--email"
                            href={`mailto:${lead.email}`}
                          >
                            Email
                          </a>
                          {lead.phone && (
                            <a
                              className="lead-action-btn lead-action-btn--call"
                              href={`tel:${lead.phone}`}
                            >
                              Call
                            </a>
                          )}
                          {lead.whatsapp && (
                            <a
                              className="lead-action-btn lead-action-btn--whatsapp"
                              href={waLink(lead.whatsapp)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              WhatsApp
                            </a>
                          )}
                          <button
                            type="button"
                            className="lead-action-btn lead-action-btn--delete"
                            disabled={deletingId === lead._id}
                            onClick={() => handleDelete(lead._id)}
                          >
                            {deletingId === lead._id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="leads-pagination">
              <button
                type="button"
                className="leads-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="leads-pagination__label">
                Page {data.page} of {data.pageCount} ({data.total} lead
                {data.total === 1 ? '' : 's'})
              </span>
              <button
                type="button"
                className="leads-pagination__btn"
                disabled={page >= data.pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuth: state.registerUser.isAuth,
  loading: state.registerUser.loading,
});

export default connect(mapStateToProps, { markLeadsViewed })(Leads);
